import React, {FC, useCallback, useState} from 'react';

import {sendDesktopAnalyticsEvent} from '@optimaxdev/analytics/desktop/lib/sendAnalyticsEvent/index';
import {ShadowCard} from '@optimaxdev/design-system';
import {connect} from 'react-redux';

import {CloudinaryImage} from 'components/cloudinaryImage/cloudinaryImage';
import {CART_BUTTON} from 'constants/analytics/events/cart';
import {ApplicationStoreType} from 'desktop/store';
import {spawnPriorityTask} from 'libs/timers/spawnPriorityTask/spawnPriorityTask';
import {ColorType} from 'types/types';

import {useTruncateText} from '../../hooks/useTruncateText/useTruncateText';
import {addAccessory} from '../../store/reducers/accessories/accessories';
import {AccessoryType} from '../../store/reducers/accessories/accessoriesType';
import {getSimpleAccessoriesByParentId} from '../../store/selectors';
import {getIsRetinaDisplay} from '../../utils/getIsRetinaDisplay/getIsRetinaDisplay';
import {AccessoryInfoTooltipConnected} from '../accessoryInfoTooltip/accessoryInfoTooltip';
import {AccessoryItemWrapper} from '../accessoryItemWrapper/accessoryItemWrapper';
import {AccessoryPrice} from '../accessoryPrice/accessoryPrice';
import {AccessoryQuantityButtonConnected} from '../accessoryQuantityButton/accessoryQuantityButton';
import {ColorBoxWithPlusIcon} from '../colorBoxWithPlusIcon/colorBoxWithPlusIcon';

import s from './newCartAccessoryItem.scss';

export type PropsType = {
    windowAccessoryId: string;
    simpleAccessories: Array<AccessoryType>;
    isLastElement: boolean;
    onViewAccessory: (accessory: AccessoryType, isLastElement: boolean) => void;
};

/**
 * Accessory item component
 */
export const NewCartAccessoryItem: FC<PropsType> = ({
    isLastElement,
    onViewAccessory,
    simpleAccessories,
}) => {
    const [selectedIndex, setSelectedIndex] = useState(0);
    const [isPopupVisible, setPopupVisibility] = useState(false);
    /**
     * Change simple product
     * @param {ColorType} color - object colors with id product
     */
    const changeSimple = useCallback(
        (color: ColorType) => {
            const newSelectedIndex = simpleAccessories.findIndex(
                ({id}: AccessoryType) => id === color.id,
            );
            setSelectedIndex(newSelectedIndex);
        },
        [simpleAccessories],
    );

    const clickOnPlus = useCallback(() => {
        spawnPriorityTask(() =>
            sendDesktopAnalyticsEvent(CART_BUTTON, {
                action: 'Accessories Section',
                label: 'More Colors',
            }),
        );
        setPopupVisibility(true);
    }, []);

    const colors = simpleAccessories.map(({color}: AccessoryType) => color);

    const accessory = simpleAccessories[selectedIndex];

    const title = useTruncateText(accessory.name);

    return simpleAccessories.length > 0 ? (
        <ShadowCard className={s.accessory} corner={5} saturate={1}>
            <AccessoryItemWrapper
                accessory={accessory}
                isLastElement={isLastElement}
                onView={onViewAccessory}
            >
                <div className={s.accessory}>
                    <div className={s.mediaWrapper}>
                        <AccessoryInfoTooltipConnected
                            accessory={accessory}
                            colorOptions={colors}
                            isPopupVisible={isPopupVisible}
                            onColorPick={changeSimple}
                            setPopupVisibility={setPopupVisibility}
                        />
                        <button
                            aria-label="open modal"
                            className={s.cloudinaryImageButton}
                            onClick={() => setPopupVisibility(true)}
                            type="button"
                        >
                            <CloudinaryImage
                                tag={
                                    getIsRetinaDisplay()
                                        ? 'h_320,w_320,c_scale,q_90'
                                        : 'h_160,w_160,c_scale,q_90'
                                }
                                alt={accessory.name}
                                height={160}
                                src={accessory.images[0].full.src}
                                width={160}
                            />
                        </button>
                    </div>
                    <div className={s.contentWrapper} data-test-name="accsItem">
                        <div className={s.colorsBlock}>
                            <ColorBoxWithPlusIcon
                                changeSimple={changeSimple}
                                colors={colors}
                                colorsLimit={4}
                                id={accessory.id}
                                onPlusClickHandler={clickOnPlus}
                            />
                        </div>
                        <p className={s.titleBlock} title={accessory.name}>
                            {title}
                            <span>
                                (
                                <AccessoryPrice
                                    price={accessory.price}
                                    specialPrice={accessory.final_price}
                                />
                                )
                            </span>
                        </p>
                        <AccessoryQuantityButtonConnected
                            accessoryId={accessory.id}
                            className={s.addButton}
                        />
                    </div>
                </div>
            </AccessoryItemWrapper>
        </ShadowCard>
    ) : null;
};

/**
 * Map state to props
 * @param {ApplicationStoreType} state - redux store
 * @param {PropsType} props - component own props
 * @returns {PropsType} props
 */
const mapStateToProps = (state: ApplicationStoreType, props: {windowAccessoryId: string}) => ({
    simpleAccessories: getSimpleAccessoriesByParentId(state, props.windowAccessoryId),
});

export const NewCartAccessoryItemConnected = connect(mapStateToProps, {addToCart: addAccessory})(
    NewCartAccessoryItem,
);
